
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFeaturedPosts, Post } from "@/lib/supabase";
import { formatDistanceToNow } from "date-fns";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Star } from "lucide-react";

export default function FeaturedPosts() {
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadFeaturedPosts = async () => {
      setIsLoading(true);
      const posts = await getFeaturedPosts(3);
      setFeaturedPosts(posts);
      setIsLoading(false);
    };
    loadFeaturedPosts();
  }, []);

  if (isLoading) {
    return <div className="py-4 flex justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blog-neon border-t-transparent shadow-[0_0_10px_rgba(67,255,255,0.5)]"></div>
      </div>;
  }

  if (featuredPosts.length === 0) {
    return null;
  }

  return (
    <div className="py-8 relative">
      <div className="flex items-center gap-2 mb-6">
        <Star className="h-6 w-6 text-blog-neon fill-blog-neon animate-pulse" />
        <h2 className="font-bold text-gradient text-2xl">Featured Posts</h2>
      </div>
      
      <div className="relative">
        <Carousel className="w-full">
          <CarouselContent>
            {featuredPosts.map(post => (
              <CarouselItem key={post.id} className="md:basis-1/2 lg:basis-1/3">
                <Link to={`/post/${post.slug}`}>
                  <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-[0_0_25px_rgba(67,255,255,0.3)] border-futuristic-border/40 hover:border-futuristic-border glass-card">
                    <div className="aspect-video w-full overflow-hidden">
                      <img 
                        src={post.thumbnail || `https://source.unsplash.com/${post.id}`} 
                        alt={post.title} 
                        className="h-full w-full object-cover transition-transform duration-500 hover:scale-110" 
                      />
                    </div>
                    <CardHeader className="p-4 pb-0">
                      <h3 className="font-bold text-lg line-clamp-2">{post.title}</h3>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {post.excerpt || post.content.substring(0, 100).replace(/<[^>]*>/g, '')}...
                      </p>
                    </CardContent>
                    <CardFooter className="p-4 pt-0 text-xs text-muted-foreground">
                      <div className="flex justify-between w-full">
                        <span>{formatDistanceToNow(new Date(post.created_at), {
                          addSuffix: true
                        })}</span>
                        <span className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-blog-neon text-blog-neon" /> 
                          <span className="text-blog-neon">Featured</span>
                        </span>
                      </div>
                    </CardFooter>
                  </Card>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          
          {/* Removed CarouselPrevious and CarouselNext buttons as requested */}
        </Carousel>
      </div>
    </div>
  );
}
